// @ts-check
/**
 * Compare list — localStorage-backed singleton store shared across the storefront.
 * Used by <oc-compare-toggle> on PDP and by the /pages/product-compare section.
 *
 * The store holds up to MAX_ITEMS product references (handle + cached display fields
 * so the compare page header can render instantly without a fetch). The compare page
 * itself re-fetches /products/<handle>.js for live price/availability/options.
 *
 * Wire-up:
 *   Store    → window.OcCompareStore (singleton, survives multiple script loads)
 *   Element  → <oc-compare-toggle data-handle="..." data-title="..." data-image="..." data-url="...">
 *   Events   → `oc:compare:change` { items }        — fired on window after any mutation
 *              `oc:compare:limit`  { max, item }    — fired when add() is rejected
 */

const STORAGE_KEY = 'oc:compare:v1';
const MAX_ITEMS = 4;
const CHANGE_EVENT = 'oc:compare:change';
const LIMIT_EVENT = 'oc:compare:limit';

/**
 * @typedef {Object} CompareItem
 * @property {string} handle
 * @property {string} title
 * @property {string} image
 * @property {string} url
 * @property {number} addedAt
 */

class OcCompareStoreImpl {
  constructor() {
    /** @type {CompareItem[]} */
    this.items = this.read();
    window.addEventListener('storage', (e) => {
      if (e.key !== STORAGE_KEY) return;
      this.items = this.read();
      this.notify();
    });
  }

  /** @returns {CompareItem[]} */
  read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((x) => x && typeof x.handle === 'string')
        .slice(0, MAX_ITEMS);
    } catch {
      return [];
    }
  }

  write() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
    } catch {}
  }

  /** @returns {CompareItem[]} */
  list() {
    return this.items.slice();
  }

  /** @returns {number} */
  count() {
    return this.items.length;
  }

  /** @returns {number} */
  max() {
    return MAX_ITEMS;
  }

  /** @param {string} handle */
  has(handle) {
    return this.items.some((x) => x.handle === handle);
  }

  /**
   * @param {Omit<CompareItem, 'addedAt'>} item
   * @returns {boolean} true if added (or already present), false if limit reached
   */
  add(item) {
    if (!item || !item.handle) return false;
    if (this.has(item.handle)) return true;
    if (this.items.length >= MAX_ITEMS) {
      window.dispatchEvent(new CustomEvent(LIMIT_EVENT, { detail: { max: MAX_ITEMS, item } }));
      return false;
    }
    this.items.push({
      handle: item.handle,
      title: item.title || '',
      image: item.image || '',
      url: item.url || '',
      addedAt: Date.now(),
    });
    this.write();
    this.notify();
    return true;
  }

  /** @param {string} handle */
  remove(handle) {
    const before = this.items.length;
    this.items = this.items.filter((x) => x.handle !== handle);
    if (this.items.length !== before) {
      this.write();
      this.notify();
    }
  }

  clear() {
    if (!this.items.length) return;
    this.items = [];
    this.write();
    this.notify();
  }

  notify() {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: this.list() } }));
  }
}

/** @type {OcCompareStoreImpl} */
const OcCompareStore = window.OcCompareStore || (window.OcCompareStore = new OcCompareStoreImpl());

class OcCompareToggle extends HTMLElement {
  connectedCallback() {
    /** @type {string} */
    this.handle = this.dataset.handle || '';
    if (!this.handle) return;

    /** @type {HTMLButtonElement | null} */
    this.button = this.querySelector('button');
    /** @type {HTMLElement | null} */
    this.labelEl = this.querySelector('[data-label]');
    /** @type {HTMLElement | null} */
    this.countEl = this.querySelector('[data-count]');
    /** @type {HTMLAnchorElement | null} */
    this.viewLink = this.querySelector('[data-view-link]');
    /** @type {HTMLElement | null} */
    this.viewCountEl = this.querySelector('[data-view-count]');

    this.idleText = this.dataset.idleText || 'Add to compare';
    this.activeText = this.dataset.activeText || 'Added to compare';
    this.fullText = this.dataset.fullText || `Compare list full (max ${OcCompareStore.max()})`;

    if (!this.button) return;

    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onLimit = this.onLimit.bind(this);

    this.button.addEventListener('click', this.onClick);
    window.addEventListener(CHANGE_EVENT, this.onChange);
    window.addEventListener(LIMIT_EVENT, this.onLimit);

    this.render();
  }

  disconnectedCallback() {
    if (this.button) this.button.removeEventListener('click', this.onClick);
    window.removeEventListener(CHANGE_EVENT, this.onChange);
    window.removeEventListener(LIMIT_EVENT, this.onLimit);
  }

  onClick() {
    if (OcCompareStore.has(this.handle)) {
      OcCompareStore.remove(this.handle);
      return;
    }
    const added = OcCompareStore.add({
      handle: this.handle,
      title: this.dataset.title || '',
      image: this.dataset.image || '',
      url: this.dataset.url || '',
    });
    if (added) this.flyToHeaderIcon();
  }

  /**
   * Mirrors the fly-to-cart animation used on ATC: clones a <fly-to-cart> element
   * pointing from this toggle button to the header compare icon, painted with the
   * product's featured image. Falls back silently if either endpoint is missing
   * (e.g. theme without <fly-to-cart> defined, or page without header compare icon).
   */
  flyToHeaderIcon() {
    if (!this.button) return;
    const headerIcon = document.querySelector('[data-oc-compare-header-icon]');
    if (!headerIcon) return;
    const image = this.dataset.image;
    if (!image) return;
    if (!customElements.get('fly-to-cart')) return;

    const fly = /** @type {any} */ (document.createElement('fly-to-cart'));
    fly.classList.add('fly-to-cart--main');
    fly.style.setProperty('background-image', `url(${image})`);
    fly.style.setProperty('--start-opacity', '0');
    fly.source = this.button;
    fly.destination = headerIcon;
    document.body.appendChild(fly);

    // Brief pulse on the header bubble once the projectile is in flight.
    headerIcon.classList.add('is-pulsing');
    setTimeout(() => headerIcon.classList.remove('is-pulsing'), 600);
  }

  onChange() {
    this.render();
  }

  /** @param {Event} e */
  onLimit(e) {
    const detail = /** @type {CustomEvent} */ (e).detail;
    if (!detail || !detail.item || detail.item.handle !== this.handle) return;
    if (!this.button) return;
    this.button.dataset.flash = 'limit';
    if (this.labelEl) this.labelEl.textContent = this.fullText;
    setTimeout(() => {
      if (!this.button) return;
      delete this.button.dataset.flash;
      this.render();
    }, 1800);
  }

  render() {
    if (!this.button) return;
    const active = OcCompareStore.has(this.handle);
    const count = OcCompareStore.count();

    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (this.labelEl) this.labelEl.textContent = active ? this.activeText : this.idleText;
    if (this.countEl) {
      if (count > 0) {
        this.countEl.hidden = false;
        this.countEl.textContent = String(count);
      } else {
        this.countEl.hidden = true;
      }
    }
    if (this.viewLink) {
      this.viewLink.hidden = count === 0;
    }
    if (this.viewCountEl) {
      this.viewCountEl.textContent = `(${count})`;
    }
  }
}

if (!customElements.get('oc-compare-toggle')) {
  customElements.define('oc-compare-toggle', OcCompareToggle);
}

/**
 * Header bubble updater — keeps every [data-oc-compare-header-icon] in sync with
 * the store's current count. Runs on every page (this script is loaded from the
 * header snippet), so the badge appears as soon as the user adds their first
 * compare item and survives cross-tab updates.
 */
function refreshHeaderBubbles() {
  const count = OcCompareStore.count();
  const icons = document.querySelectorAll('[data-oc-compare-header-icon]');
  icons.forEach((/** @type {Element} */ icon) => {
    const bubble = /** @type {HTMLElement | null} */ (icon.querySelector('[data-oc-compare-bubble]'));
    const countEl = /** @type {HTMLElement | null} */ (icon.querySelector('[data-oc-compare-bubble-count]'));
    if (bubble) bubble.classList.toggle('hidden', count === 0);
    if (countEl) countEl.textContent = String(count);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshHeaderBubbles, { once: true });
} else {
  refreshHeaderBubbles();
}
window.addEventListener(CHANGE_EVENT, refreshHeaderBubbles);
